import React from 'react';
import moment from 'moment';
import { setDefaultLocale } from 'react-datepicker';
import { LanguageCode, isSupportedLanguage } from 'localization/Localization';
import Throw from 'util/Throw';
import UserData from 'models/UserData';

export type LocaleStrings = { [key: string]: string };

const LocalizationContext = React.createContext<LocaleStrings>({});
export default LocalizationContext;
export const LocalizationConsumer = LocalizationContext.Consumer;

const fallbackLang = 'en';

export let getLocaleStrings: () => LocaleStrings = Throw.NoProvider;
export let changeLocale: (lang: LanguageCode) => void = Throw.NoProvider;
export let getCurrentLanguage: () => LanguageCode = Throw.NoProvider;

export const LocalizationProvider = (props: { children: any, user: UserData }) => {
    const [lang, setLang] = React.useState<LanguageCode>();
    const [localeData, setLocaleData] = React.useState<LocaleStrings>();

    const userLang = React.useMemo(() => props.user.language, [props.user.language]);

    // React to user language changes
    React.useEffect(() => {
        let newLanguage: LanguageCode | undefined = undefined;

        if (userLang !== undefined && isSupportedLanguage(userLang)) {
            newLanguage = userLang.substr(0, 2) as LanguageCode;
        }

        // Try browser language
        if (newLanguage === undefined) {
            if (isSupportedLanguage(window.navigator.language || '')) {
                newLanguage = window.navigator.language.substr(0, 2) as LanguageCode;
            }
        }

        // Fallback to default
        if (newLanguage === undefined) {
            newLanguage = fallbackLang;
        }

        if (newLanguage !== undefined) {
           setLang(newLanguage);
        }
    }, [userLang])


    // React to language changes
    React.useEffect(() => {
        if (lang === undefined)
            return;

        moment.locale(window.navigator.language || lang);

        setDefaultLocale(lang);

        console.log("Loading localization: " + lang);

        const abortController = new AbortController();
        const get = async () => {
            try {
                const result = await fetch('localization/' + lang, { signal: abortController.signal });
                if (result.status === 200) {
                    const json = await result.json();
                    if (typeof json === 'object') {
                        setLocaleData(json)
                    }
                }
            }
            catch (reason) {
                console.error(reason)
            }
        }

        get();
        return () => abortController.abort();
    }, [lang])

    // Setup functions
    getLocaleStrings = () => localeData || {};
    getCurrentLanguage = () => lang || fallbackLang;
    changeLocale = setLang;

    return (
        <LocalizationContext.Provider value={localeData || {}}>
            {localeData !== undefined && props.children}
        </LocalizationContext.Provider>
    )
}